// Terms of Service page layout data — follows the same pattern as
// footerLayouts.constants.js, but for a standalone page (/terms) rather than
// an embeddable section. Single layout only (no visual-style variants like
// hero/faq have) — the admin gets full rich-text control over one page's
// content, not a choice between layout styles.
//
// `content`'s `en` seed is meelz-client-v2's real, live Terms of Service
// copy (src/app/[locale]/(app)/terms/page.tsx), converted verbatim to HTML
// matching the RichTextEditorField's Tiptap schema (h2/h3, p, ul/li, strong,
// em, a — no h1, since the page title is owned by the Default/Structural
// page shell, not admin content). `ru`/`ar` are left absent, not seeded with
// duplicated English text — the real pages are English-only today; an
// admin/translator adds those later, same convention as any other
// unmet `translatable` field left blank until authored.
//
// The PDF download link ("/files/TOC-RPT.pdf") is NOT part of this content —
// it stays hardcoded in the client's Default fallback only (see plan
// Decision 6), so it is intentionally absent from the HTML below.

const TERMS_PAGE_CONTENT_EN = `
<h2>1. General Provisions</h2>
<p><strong>1.1.</strong> This document constitutes a legally binding public offer in accordance with the applicable laws of the United Arab Emirates.</p>
<p><strong>1.2.</strong> Acceptance of this Offer occurs when the Client places an order and/or completes payment for the Services.</p>
<p><strong>1.3.</strong> Acceptance of this Offer signifies full and unconditional agreement with all terms and conditions contained herein.</p>
<p><strong>1.4.</strong> The Provider reserves the right to amend or update the terms of this Offer at any time. The updated version shall become effective upon publication on the Provider's official website.</p>
<h2>2. Subject of the Agreement</h2>
<p><strong>2.1.</strong> The Provider offers services related to the preparation and delivery of balanced meal plans based on the program selected by the Client (hereinafter referred to as the "Services").</p>
<p><strong>2.2.</strong> The description of meal plans, nutritional values, ingredients, and program conditions are published on the official website <a href="https://meelz.ae">https://meelz.ae</a> or communicated through official Provider channels including WhatsApp, Instagram, or Telegram.</p>
<p><strong>2.3.</strong> Meal plans are prepared according to nutritional guidelines but are not intended to replace professional medical advice.</p>
<h2>3. Order Procedure</h2>
<p><strong>3.1.</strong> Orders may be placed through:</p>
<ul>
<li>the official website</li>
<li>WhatsApp</li>
<li>Instagram</li>
<li>Telegram</li>
<li>phone number listed on the website</li>
</ul>
<p><strong>3.2.</strong> The Client independently selects:</p>
<ul>
<li>the meal program</li>
<li>calorie plan</li>
<li>duration of the meal plan</li>
</ul>
<p><strong>3.3.</strong> An order shall be considered confirmed once payment is successfully received and acknowledged by the Provider.</p>
<p><strong>3.4.</strong> The Provider reserves the right to refuse an order in cases of operational limitations, incorrect information provided by the Client, or safety concerns.</p>
<h2>4. Pricing and Payment</h2>
<p><strong>4.1.</strong> The cost of Services depends on:</p>
<ul>
<li>number of delivery days</li>
<li>selected calorie plan</li>
<li>selected meal program</li>
</ul>
<p><strong>4.2.</strong> Payments may be made via:</p>
<ul>
<li>online payment gateways (including Stripe or other licensed UAE payment providers)</li>
<li>installment payment services where available</li>
</ul>
<p><strong>4.3.</strong> By making a payment, the Client confirms that they are authorized to use the selected payment method.</p>
<p><strong>4.4.</strong> The Provider shall not be responsible for delays, errors, or failures caused by third-party payment processors.</p>
<h2>5. Refund and Cancellation Policy</h2>
<p><strong>5.1.</strong> Due to the nature of food preparation and logistics, meal plans are generally non-refundable.</p>
<p><strong>5.2.</strong> Refund requests may be considered solely at the discretion of RPT Restaurant LLC.</p>
<p><strong>5.3.</strong> If a refund request is approved, a 20% cancellation and processing fee will be applied to the unused portion of the meal plan.</p>
<p><strong>5.4.</strong> Approved refunds will be processed within 14 business days from the date of written confirmation.</p>
<p><strong>5.5.</strong> Trial meal plans, promotional offers, and discounted packages are strictly non-refundable.</p>
<h2>6. Delivery Postponement / Freezing</h2>
<p><strong>6.1.</strong> The Client may request to temporarily freeze or postpone meal deliveries.</p>
<p><strong>6.2.</strong> Such request must be submitted no later than 12:00 PM on the day prior to delivery.</p>
<p><strong>6.3.</strong> If notice is not provided within the required timeframe, the delivery day shall be considered fulfilled and non-refundable.</p>
<h2>7. Force Majeure</h2>
<p><strong>7.1.</strong> The Provider shall not be liable for any delay, interruption, or failure to perform obligations under this Offer due to circumstances beyond its reasonable control.</p>
<p><strong>7.2.</strong> Such circumstances include, but are not limited to:</p>
<ul>
<li>war or armed conflict</li>
<li>civil unrest or terrorism</li>
<li>government restrictions</li>
<li>natural disasters</li>
<li>extreme weather conditions</li>
<li>transportation disruptions</li>
<li>epidemics or public health emergencies</li>
</ul>
<p><strong>7.3.</strong> In such cases, if delivery becomes impossible or unsafe, the current delivery day and any remaining scheduled deliveries shall be considered fulfilled and shall not be subject to refund.</p>
<p><strong>7.4.</strong> The Provider reserves the right to suspend or modify deliveries during such events for safety reasons.</p>
<h2>8. Responsibilities of the Parties</h2>
<p><strong>8.1.</strong> The Provider shall take reasonable measures to ensure the quality and safety of the meals provided.</p>
<p><strong>8.2.</strong> The Client agrees to provide accurate delivery information and maintain availability during the delivery time.</p>
<p><strong>8.3.</strong> The Provider shall not be responsible for failed deliveries caused by:</p>
<ul>
<li>incorrect address</li>
<li>inability to contact the Client</li>
<li>absence of the Client at the delivery location</li>
</ul>
<p><strong>8.4.</strong> The Client is responsible for informing the Provider of any food allergies or dietary restrictions prior to placing an order.</p>
<p><strong>8.5.</strong> The Provider shall not be liable for allergic reactions or dietary incompatibilities if the Client fails to disclose such information.</p>
<h2>9. Minimum Order Period</h2>
<p><strong>9.1.</strong> The minimum order period for Services is one (1) day, unless otherwise specified in a promotional offer.</p>
<h2>10. Governing Law and Dispute Resolution</h2>
<p><strong>10.1.</strong> This Offer shall be governed by the laws of the United Arab Emirates.</p>
<p><strong>10.2.</strong> Any disputes arising out of or in connection with this Offer shall be subject to the jurisdiction of the competent courts of the United Arab Emirates.</p>
<p><strong>10.3.</strong> This Public Offer remains valid until revoked by the Provider.</p>
`.trim();

export const TERMS_PAGE_DEFAULT_PROPS = {
    backgroundColor: "#ffffff",
    textColor:       "#1E1E1E",
    content:         { en: TERMS_PAGE_CONTENT_EN },
};

// CK: first brand to need a real, brand-owned Terms entry, since CK had no
// prior Terms page of its own to extract from (confirmed via repo-wide
// grep — no /terms route, no hardcoded content, nothing). Seeded from
// meelz's real content above as a starting draft — same real legal
// structure, admin edits in the company/jurisdiction specifics before
// publishing. A fresh, independent copy (not a reference to meelz's data) —
// CK's admin edits don't affect meelz's `default` entry and vice versa,
// same non-sharing relationship as ourAdvantage/faq/thankYou's own `ck`
// keys. Colors match CK's own real palette (per hero's already-extracted
// values), not meelz's white/dark-gray defaults.
export const TERMS_PAGE_CK_DEFAULT_PROPS = {
    backgroundColor: "#ffffff",
    textColor:       "#46298A",
    content:         { en: TERMS_PAGE_CONTENT_EN },
};

// Single "default" layout id only — no layout picker/dropdown shown (there
// is nothing to choose between; every brand just gets its own real content
// automatically). CK's real content (TERMS_PAGE_CK_DEFAULT_PROPS above)
// used to be reachable only via a separate, easily-mis-selected
// "Terms of Service (CK)" layout option here — removed in favor of
// FULL_CONTENT_OVERRIDE_BY_KEY_AND_BRAND (componentRegistry.js), same
// pattern already used for faq/quiz's own former "(CK)" layout options.
export const TERMS_PAGE_LAYOUTS = {
    default: { label: "Terms of Service", defaultProps: TERMS_PAGE_DEFAULT_PROPS },
};

export const TERMS_PAGE_SCHEMA = [
    { key: "content",         label: "Page content",     type: "richText", translatable: true },
    { key: "backgroundColor", label: "Background color", type: "color" },
    { key: "textColor",       label: "Text color",       type: "color" },
];
